import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Save, Camera, CheckCircle2, Car, ArrowLeftRight } from 'lucide-react';
import { api } from '../../services/api';
import InterestedInStep2 from './InterestedInStep2';
import { MODELS_BY_MAKE, TRIM_LEVELS } from '../../superadmin/data/makeModelTrimData';
import { US_STATES } from '../../data/usStates';
import { useConfirm } from '../../contexts/ConfirmContext';
import '../AllCss/VisitUsTab.css';

const CAR_MAKES = Object.keys(MODELS_BY_MAKE).sort((a, b) => a.localeCompare(b));

const YEAR_OPTIONS = Array.from(
  { length: new Date().getFullYear() + 2 - 1990 },
  (_, i) => new Date().getFullYear() + 1 - i
);

const CONDITION_OPTIONS = [
  { value: 'new', label: 'New' },
  { value: 'used', label: 'Used' },
  { value: 'certified', label: 'Certified Pre-Owned' },
];

const PREFERRED_CONTACT_OPTIONS = [
  { value: '', label: 'No preference' },
  { value: 'phone', label: 'Phone call' },
  { value: 'text', label: 'Text message' },
  { value: 'email', label: 'Email' },
];

const TITLE_STATUS_OPTIONS = [
  { value: '', label: 'Select title status' },
  { value: 'clean', label: 'Clean title' },
  { value: 'lien', label: 'Lien / loan payoff' },
  { value: 'salvage', label: 'Salvage' },
  { value: 'rebuilt', label: 'Rebuilt' },
];

const INTEREST_TYPE_OPTIONS = [
  {
    value: 'purchase',
    icon: Car,
    title: 'Buy a vehicle',
    description: 'Describe what you want — then continue to secure payment (sourcing fee) to start our auction search.',
  },
  {
    value: 'sell',
    icon: ArrowLeftRight,
    title: 'Sell or trade',
    description: 'Tell us about your car for a trade-in or cash offer when you visit.',
  },
];

function normalizeDollarWholeInput(raw, maxDigits = 9) {
  return String(raw ?? '').replace(/\D/g, '').slice(0, maxDigits);
}

function formatDollarWholeDisplay(stored) {
  if (stored === '' || stored == null) return '';
  const d = String(stored).replace(/\D/g, '');
  if (!d) return '';
  return Number(d).toLocaleString('en-US', { maximumFractionDigits: 0 });
}

function formatPhoneDisplay(phone) {
  const digits = String(phone ?? '').replace(/\D/g, '');
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return String(phone ?? '').trim();
}

/** Contact fields copied from the logged-in user profile (register or Personal Info). */
function profileContactFields(user) {
  return {
    guestName: user?.fullName?.trim() || '',
    guestEmail: user?.email?.trim() || '',
    guestPhone: formatPhoneDisplay(user?.phone),
    guestAddress: user?.address?.trim() || '',
    guestAddress2: user?.address2?.trim() || '',
    guestCity: user?.city?.trim() || '',
    guestState: user?.state?.trim() || '',
    guestZipCode: user?.zipCode?.trim() || '',
  };
}

function isProfileContactFieldLocked(fieldName, user) {
  const profile = profileContactFields(user);
  return Boolean(profile[fieldName]);
}

function mergeProfileContact(form, user) {
  const profile = profileContactFields(user);
  const next = { ...form };
  for (const [key, value] of Object.entries(profile)) {
    if (value) next[key] = value;
  }
  return next;
}

function buildEmptyForm(user, initialCarId = '') {
  const contact = profileContactFields(user);
  return {
    type: '',
    ...contact,
    guestPhone2: '',
    preferredContact: '',
    carId: initialCarId,
    budgetMax: '',
    interestMake: '',
    interestModel: '',
    interestTrim: '',
    interestYearMin: '',
    interestYearMax: '',
    interestMileageMax: '',
    sellYear: '',
    sellMake: '',
    sellModel: '',
    sellTrim: '',
    sellMileage: '',
    sellCondition: 'used',
    sellVin: '',
    sellColor: '',
    sellExpectedPrice: '',
    sellTitleStatus: '',
    notes: '',
  };
}

/** Exactly one of step1 / step2 is true whenever pending exists — prevents blank Interested In tab. */
function resolveWizardScreens(pendingStep2, editStep1) {
  const hasPending = Boolean(pendingStep2);
  const showStep2 = hasPending && !editStep1;
  const showStep1 = !hasPending || editStep1;
  return {
    showStep1,
    showStep2,
    activeStep: showStep2 ? 2 : 1,
  };
}

function buildInterestedInVehicleSummary(walkIn) {
  if (!walkIn) return 'your vehicle';
  if (walkIn.type === 'sell') {
    const parts = [walkIn.sellYear, walkIn.sellMake, walkIn.sellModel].filter(Boolean);
    return parts.length ? parts.join(' ') : 'your vehicle';
  }
  const trim =
    walkIn.interestTrim && walkIn.interestTrim !== 'Any' ? walkIn.interestTrim : null;
  const parts = [walkIn.interestMake, walkIn.interestModel, trim].filter(Boolean);
  return parts.length ? parts.join(' ') : 'your vehicle';
}

function formatInterestedInApiError(err, fallback) {
  const code = err.response?.data?.code;
  const message = err.response?.data?.message;
  const detail = err.response?.data?.detail;
  if (code === 'DATABASE_ERROR') {
    return (
      message ||
      'Database error saving your interest. Our team is applying a schema update — wait 2 minutes, refresh, and try again.'
    );
  }
  if (code === 'SCHEMA_MIGRATION' && detail) {
    return `${message} (${detail})`;
  }
  return message || fallback;
}

function walkInToForm(walkIn, user, initialCarId) {
  const base = buildEmptyForm(user, initialCarId);
  if (!walkIn) return base;
  const profile = profileContactFields(user);
  return {
    ...base,
    type: walkIn.type === 'purchase' || walkIn.type === 'sell' ? walkIn.type : '',
    guestName: profile.guestName || walkIn.guestName || base.guestName,
    guestEmail: profile.guestEmail || walkIn.guestEmail || base.guestEmail,
    guestPhone: profile.guestPhone || walkIn.guestPhone || base.guestPhone,
    guestPhone2: walkIn.guestPhone2 || '',
    guestAddress: profile.guestAddress || walkIn.guestAddress || base.guestAddress,
    guestAddress2: profile.guestAddress2 || walkIn.guestAddress2 || base.guestAddress2,
    guestCity: profile.guestCity || walkIn.guestCity || base.guestCity,
    guestState: profile.guestState || walkIn.guestState || base.guestState,
    guestZipCode: profile.guestZipCode || walkIn.guestZipCode || base.guestZipCode,
    preferredContact: walkIn.preferredContact || '',
    carId: walkIn.carId || initialCarId || '',
    budgetMax: walkIn.budgetMax != null ? String(Math.trunc(Number(walkIn.budgetMax))) : '',
    interestMake: walkIn.interestMake || '',
    interestModel: walkIn.interestModel || '',
    interestTrim: walkIn.interestTrim || '',
    interestYearMin: walkIn.interestYearMin != null ? String(walkIn.interestYearMin) : '',
    interestYearMax: walkIn.interestYearMax != null ? String(walkIn.interestYearMax) : '',
    interestMileageMax: walkIn.interestMileageMax != null ? String(walkIn.interestMileageMax) : '',
    sellYear: walkIn.sellYear != null ? String(walkIn.sellYear) : '',
    sellMake: walkIn.sellMake || '',
    sellModel: walkIn.sellModel || '',
    sellTrim: walkIn.sellTrim || '',
    sellMileage: walkIn.sellMileage != null ? String(walkIn.sellMileage) : '',
    sellCondition: walkIn.sellCondition || 'used',
    sellVin: walkIn.sellVin || '',
    sellColor: walkIn.sellColor || '',
    sellExpectedPrice:
      walkIn.sellExpectedPrice != null ? String(Math.trunc(Number(walkIn.sellExpectedPrice))) : '',
    sellTitleStatus: walkIn.sellTitleStatus || '',
    notes: walkIn.notes || '',
  };
}

function VisitUsTab({ user, initialCarId = '' }) {
  const confirmDialog = useConfirm();
  const [searchParams, setSearchParams] = useSearchParams();
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [completionModal, setCompletionModal] = useState(null);
  const [activePreReg, setActivePreReg] = useState(null);
  const [pendingStep2, setPendingStep2] = useState(null);
  /** When true, user chose "Back to step 1" while a pending step-2 walk-in exists. */
  const [editStep1, setEditStep1] = useState(false);
  const [form, setForm] = useState(() => buildEmptyForm(user, initialCarId));
  const formSectionRef = useRef(null);
  const step2SectionRef = useRef(null);
  /** After step-1 save, ignore one fetch that still returns null pending (race / replica lag). */
  const protectPendingStep2Ref = useRef(false);

  const returnFromSquare = searchParams.get('step2') === 'return';
  const walkInIdFromUrl = searchParams.get('walkInId') || '';

  const [idFrontFile, setIdFrontFile] = useState(null);
  const [idBackFile, setIdBackFile] = useState(null);
  const [idFrontPreview, setIdFrontPreview] = useState('');
  const [idBackPreview, setIdBackPreview] = useState('');

  const sellModelOptions = useMemo(() => {
    if (!form.sellMake || !MODELS_BY_MAKE[form.sellMake]) return [];
    return MODELS_BY_MAKE[form.sellMake];
  }, [form.sellMake]);

  const interestModelOptions = useMemo(() => {
    if (!form.interestMake || !MODELS_BY_MAKE[form.interestMake]) return [];
    return MODELS_BY_MAKE[form.interestMake];
  }, [form.interestMake]);

  const interestTrimOptions = useMemo(() => {
    if (!form.interestMake || !form.interestModel) return [];
    const key = `${form.interestMake}|${form.interestModel}`;
    return TRIM_LEVELS[key] || [];
  }, [form.interestMake, form.interestModel]);

  const { showStep1, showStep2, activeStep } = useMemo(
    () => resolveWizardScreens(pendingStep2, editStep1),
    [pendingStep2, editStep1]
  );

  useEffect(() => {
    if (!pendingStep2) setEditStep1(false);
  }, [pendingStep2]);

  useEffect(() => {
    if (returnFromSquare && pendingStep2) setEditStep1(false);
  }, [returnFromSquare, pendingStep2]);

  const handleStep2Complete = (_message, meta = {}) => {
    const walkIn = meta.walkIn || pendingStep2;
    const interestType = walkIn?.type || form.type;

    setCompletionModal({
      vehicleSummary: buildInterestedInVehicleSummary(walkIn),
      emailSent: meta.emailSent,
      email: user?.email || form.guestEmail || walkIn?.guestEmail || null,
      isPurchase: interestType === 'purchase',
    });
    setSuccess('');
    setError('');
    setPendingStep2(null);
    setEditStep1(false);
    setForm(buildEmptyForm(user, initialCarId));
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('step2');
      next.delete('walkInId');
      next.delete('squareMock');
      return next;
    });
    fetchData().then(() => {
      requestAnimationFrame(() => {
        formSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const carsRes = await api.get('/cars', { params: { status: 'available', limit: 500 } });
      const carsData = carsRes.data?.cars || carsRes.data?.data?.cars || [];
      setCars(Array.isArray(carsData) ? carsData : []);

      let preReg = null;
      let pending = null;
      try {
        const myRes = await api.get('/interested-in/mine');
        preReg = myRes.data?.data?.activePreRegistration || null;
        pending = myRes.data?.data?.pendingStep2 || null;
      } catch (mineErr) {
        console.error('Error loading visit pre-registration:', mineErr);
        setError(
          formatInterestedInApiError(
            mineErr,
            'Could not load your interest details. The API database may need the Interested In migration — redeploy the server or run db:apply-interested-in-step2 on production.'
          )
        );
      }
      setActivePreReg(preReg);
      if (pending) {
        protectPendingStep2Ref.current = false;
        setPendingStep2(pending);
        setForm(walkInToForm(pending, user, initialCarId));
        if (returnFromSquare) setEditStep1(false);
      } else if (!protectPendingStep2Ref.current) {
        setPendingStep2(null);
        setForm(buildEmptyForm(user, initialCarId));
        setEditStep1(false);
      }
    } catch (err) {
      console.error('Error loading visit inventory:', err);
      setForm(buildEmptyForm(user, initialCarId));
      if (!err.response) {
        setError(
          'Cannot load inventory — the API may be offline. Start the server (cd server && npm run dev) and refresh.'
        );
      } else if (err.response.status === 401) {
        setError('Your session expired. Sign out, sign in again, then try Continue to secure payment.');
      } else {
        setError(formatInterestedInApiError(err, 'Could not load the Interested In page.'));
      }
    } finally {
      setLoading(false);
    }
  }, [user, initialCarId, returnFromSquare]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Keep contact fields in sync when profile is updated (register or Personal Info tab).
  useEffect(() => {
    if (!user) return;
    setForm((current) => mergeProfileContact(current, user));
  }, [
    user?.fullName,
    user?.email,
    user?.phone,
    user?.address,
    user?.address2,
    user?.city,
    user?.state,
    user?.zipCode,
  ]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => {
      const next = {
        ...f,
        [name]: name === 'budgetMax' || name === 'sellExpectedPrice' ? normalizeDollarWholeInput(value) : value,
      };
      if (name === 'sellMake') {
        next.sellModel = '';
        next.sellTrim = '';
      }
      if (name === 'interestMake') {
        next.interestModel = '';
      }
      return next;
    });
  };

  const handleTypeChange = (e) => {
    selectInterestType(e.target.value);
  };

  const selectInterestType = (type) => {
    if (type !== 'purchase' && type !== 'sell') return;
    const contact = profileContactFields(user);
    setForm((f) => ({
      ...buildEmptyForm(user, initialCarId),
      ...contact,
      type,
      guestPhone2: f.guestPhone2,
      preferredContact: f.preferredContact,
      notes: f.notes,
      carId: type === 'purchase' ? f.carId || initialCarId : '',
      budgetMax: type === 'purchase' ? f.budgetMax : '',
      interestMake: type === 'purchase' ? f.interestMake : '',
      interestModel: type === 'purchase' ? f.interestModel : '',
      interestYearMin: type === 'purchase' ? f.interestYearMin : '',
      interestYearMax: type === 'purchase' ? f.interestYearMax : '',
      interestMileageMax: type === 'purchase' ? f.interestMileageMax : '',
    }));
  };

  const applyIdFile = (side, file) => {
    if (!file || !file.type.startsWith('image/')) return;
    if (side === 'front') {
      if (idFrontPreview) URL.revokeObjectURL(idFrontPreview);
      setIdFrontFile(file);
      setIdFrontPreview(URL.createObjectURL(file));
    } else {
      if (idBackPreview) URL.revokeObjectURL(idBackPreview);
      setIdBackFile(file);
      setIdBackPreview(URL.createObjectURL(file));
    }
  };

  const handleIdFile = (side, e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    applyIdFile(side, file);
  };

  const uploadIdImageFile = async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    const { data } = await api.post('/interested-in/upload-id-image', fd);
    if (!data.success) throw new Error(data.message || 'ID image upload failed');
    return data.data.url;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!form.guestName.trim()) {
      setError('Name is required');
      return;
    }
    if (form.type !== 'purchase' && form.type !== 'sell') {
      setError('Please choose whether you want to buy a vehicle or sell/trade.');
      return;
    }
    if (form.type === 'sell') {
      if (!form.sellYear || !form.sellMake || !form.sellModel) {
        setError('Year, make, and model are required');
        return;
      }
      if (!form.sellMileage && form.sellMileage !== 0) {
        setError('Mileage is required');
        return;
      }
    }

    setSubmitting(true);
    try {
      let idFrontImageUrl;
      let idBackImageUrl;
      if (idFrontFile) {
        idFrontImageUrl = await uploadIdImageFile(idFrontFile);
      }
      if (idBackFile) {
        idBackImageUrl = await uploadIdImageFile(idBackFile);
      }

      const payload = {
        type: form.type,
        guestName: form.guestName.trim(),
        guestEmail: form.guestEmail.trim() || undefined,
        guestPhone: form.guestPhone.trim() || undefined,
        guestPhone2: form.guestPhone2.trim() || undefined,
        guestAddress: form.guestAddress.trim() || undefined,
        guestAddress2: form.guestAddress2.trim() || undefined,
        guestCity: form.guestCity.trim() || undefined,
        guestState: form.guestState || undefined,
        guestZipCode: form.guestZipCode.trim() || undefined,
        preferredContact: form.preferredContact || undefined,
        notes: form.notes.trim() || undefined,
        idFrontImageUrl,
        idBackImageUrl,
      };

      if (form.type === 'purchase') {
        if (form.carId) payload.carId = form.carId;
        if (form.budgetMax) payload.budgetMax = Number(normalizeDollarWholeInput(form.budgetMax));
        if (form.interestMake) payload.interestMake = form.interestMake;
        if (form.interestModel) payload.interestModel = form.interestModel;
        if (form.interestTrim) payload.interestTrim = form.interestTrim;
        if (form.interestYearMin) payload.interestYearMin = Number(form.interestYearMin);
        if (form.interestYearMax) payload.interestYearMax = Number(form.interestYearMax);
        if (form.interestMileageMax) {
          payload.interestMileageMax = Number(String(form.interestMileageMax).replace(/,/g, ''));
        }
      } else {
        payload.sellYear = Number(form.sellYear);
        payload.sellMake = form.sellMake;
        payload.sellModel = form.sellModel;
        payload.sellTrim = form.sellTrim || undefined;
        payload.sellMileage = Number(String(form.sellMileage).replace(/,/g, ''));
        payload.sellCondition = form.sellCondition;
        payload.sellVin = form.sellVin.trim().toUpperCase() || undefined;
        payload.sellColor = form.sellColor.trim() || undefined;
        payload.sellExpectedPrice = form.sellExpectedPrice
          ? Number(normalizeDollarWholeInput(form.sellExpectedPrice))
          : undefined;
        payload.sellTitleStatus = form.sellTitleStatus || undefined;
      }

      const { data } = await api.post('/interested-in', payload);
      if (!data?.success || !data?.data?.walkIn) {
        throw new Error(data?.message || 'Unexpected server response — try again.');
      }
      protectPendingStep2Ref.current = true;
      setPendingStep2(data.data.walkIn);
      setEditStep1(false);
      setIdFrontFile(null);
      setIdBackFile(null);
      if (idFrontPreview) URL.revokeObjectURL(idFrontPreview);
      if (idBackPreview) URL.revokeObjectURL(idBackPreview);
      setIdFrontPreview('');
      setIdBackPreview('');
      requestAnimationFrame(() => {
        step2SectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    } catch (err) {
      if (!err.response) {
        const isUpload = /upload|ID image/i.test(String(err.message || ''));
        setError(
          isUpload
            ? err.message || 'ID photo upload failed — try again without photos or check that the API is running.'
            : 'Cannot reach the server. Start the API (cd server && npm run dev) and refresh this page.'
        );
      } else if (err.response.status === 401) {
        setError('Your session expired. Sign out, sign in again, then try Continue to secure payment.');
      } else {
        setError(
          formatInterestedInApiError(err, err.message || 'Failed to save your interest')
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async () => {
    const ok = await confirmDialog({
      title: 'Remove interest',
      description: 'Remove your saved interest?',
      confirmLabel: 'Remove',
      destructive: true,
    });
    if (!ok) return;
    setCancelling(true);
    setError('');
    try {
      await api.delete('/interested-in/active');
      setActivePreReg(null);
      setPendingStep2(null);
      setEditStep1(false);
      setForm(buildEmptyForm(user, initialCarId));
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('step2');
        next.delete('walkInId');
        next.delete('squareMock');
        return next;
      });
      setSuccess('Interest removed');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to cancel');
    } finally {
      setCancelling(false);
    }
  };

  const availableCars = cars.filter((c) => c.status === 'available');

  if (loading) {
    return (
      <div className="visit-us-loading">
        <p>Loading…</p>
      </div>
    );
  }

  return (
    <div className="VisitUsTab">
      <div className="visit-us-header">
        <div>
          <h2 className="visit-us-title">Interested In</h2>
          <p className="visit-us-subtitle">
            Tell us what you want to buy or sell — we&apos;ll have your details ready when you stop by. No appointment
            needed.
          </p>
        </div>
      </div>

      {error && <div className="visit-us-alert visit-us-alert-error">{error}</div>}
      {success && showStep1 && (
        <div className="visit-us-alert visit-us-alert-success">{success}</div>
      )}

      {completionModal && (
        <div
          className="visit-us-complete-overlay"
          role="presentation"
          onClick={() => setCompletionModal(null)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setCompletionModal(null);
          }}
        >
          <div
            className="visit-us-complete-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="visit-us-complete-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="visit-us-complete-icon" aria-hidden>
              <CheckCircle2 size={40} />
            </div>
            <h2 id="visit-us-complete-title" className="visit-us-complete-title">
              {completionModal.isPurchase ? 'Payment confirmed' : 'Request confirmed'}
            </h2>
            {completionModal.isPurchase ? (
              <>
                <p className="visit-us-complete-message">
                  We&apos;re now searching auction inventory for your{' '}
                  <strong>{completionModal.vehicleSummary}</strong>.
                </p>
                <p className="visit-us-complete-note">
                  Sourcing fee is non-refundable and starts the search process per our agreement.
                </p>
              </>
            ) : (
              <p className="visit-us-complete-message">
                Your card is on file for your <strong>{completionModal.vehicleSummary}</strong>. Bring
                your vehicle when you visit — no charge was made today.
              </p>
            )}
            {completionModal.emailSent && completionModal.email && (
              <p className="visit-us-complete-email">
                Details sent to {completionModal.email}
              </p>
            )}
            {completionModal.emailSent === false && completionModal.email && (
              <p className="visit-us-complete-email visit-us-complete-email--warn">
                We could not send a confirmation email — please save your request details.
              </p>
            )}
            <button
              type="button"
              className="visit-us-complete-btn"
              onClick={() => setCompletionModal(null)}
              autoFocus
            >
              Got it
            </button>
          </div>
        </div>
      )}

      {pendingStep2 && (
        <div className="visit-us-step-indicator">
          <span className={activeStep === 1 ? 'is-active' : ''}>1. Your details</span>
          <span aria-hidden>→</span>
          <span className={activeStep === 2 ? 'is-active' : ''}>
            2. {(pendingStep2.type || form.type) === 'sell' ? 'Card on file' : 'Payment'}
          </span>
        </div>
      )}

      {activePreReg && pendingStep2 && (
        <p className="visit-us-pending-with-complete">
          You have a completed request (ref{' '}
          <code>{activePreReg.id.slice(0, 8).toUpperCase()}</code>). Finish payment for your new request
          below.
        </p>
      )}

      {pendingStep2 && editStep1 && (
        <div className="visit-us-continue-step2">
          <p>Step 1 is saved. Continue to payment when you are ready.</p>
          <button type="button" className="visit-us-continue-step2-btn" onClick={() => setEditStep1(false)}>
            Continue to step 2 — {(pendingStep2.type || form.type) === 'sell' ? 'card on file' : 'payment'}
          </button>
        </div>
      )}

      {showStep2 && pendingStep2 && (
        <div ref={step2SectionRef}>
          <InterestedInStep2
            pendingWalkIn={pendingStep2}
            user={user}
            onComplete={handleStep2Complete}
            returnFromSquare={returnFromSquare}
            walkInIdFromUrl={walkInIdFromUrl}
          />
        </div>
      )}

      {showStep1 && (
      <form className="visit-us-form" onSubmit={handleSubmit} ref={formSectionRef}>
        <section className="visit-us-section visit-us-section-interest">
          <h3>What are you interested in?</h3>
          <p className="visit-us-hint">Choose one — you can update this anytime before you visit.</p>
          <fieldset className="visit-us-interest-fieldset">
            <legend className="visit-us-sr-only">What are you interested in?</legend>
            <div className="visit-us-interest-cards">
              {INTEREST_TYPE_OPTIONS.map((opt) => {
                const Icon = opt.icon;
                const selected = form.type === opt.value;
                return (
                  <label
                    key={opt.value}
                    className={`visit-us-interest-card${selected ? ' visit-us-interest-card--selected' : ''}`}
                  >
                    <input
                      type="radio"
                      name="visitType"
                      value={opt.value}
                      checked={selected}
                      onChange={handleTypeChange}
                      className="visit-us-interest-input"
                    />
                    <span className="visit-us-interest-icon" aria-hidden>
                      <Icon size={22} strokeWidth={1.75} />
                    </span>
                    <span className="visit-us-interest-text">
                      <span className="visit-us-interest-title">{opt.title}</span>
                      <span className="visit-us-interest-desc">{opt.description}</span>
                    </span>
                    <span className="visit-us-interest-check" aria-hidden>
                      {selected && <CheckCircle2 size={18} />}
                    </span>
                  </label>
                );
              })}
            </div>
          </fieldset>
        </section>

        <section className="visit-us-section">
          <h3>Contact information</h3>
          <p className="visit-us-hint">
            Pulled from your profile when available — no need to re-enter address or name.{' '}
            <Link to="/profile?tab=personal" className="visit-us-profile-link">
              Update in Personal Info
            </Link>
          </p>
          <div className="visit-us-grid visit-us-grid-2">
            <div className="visit-us-field">
              <label htmlFor="guestName">Full name *</label>
              <input
                id="guestName"
                name="guestName"
                value={form.guestName}
                onChange={handleChange}
                readOnly={isProfileContactFieldLocked('guestName', user)}
                className={isProfileContactFieldLocked('guestName', user) ? 'visit-us-input-profile' : undefined}
                required
              />
            </div>
            <div className="visit-us-field">
              <label htmlFor="guestEmail">Email</label>
              <input
                id="guestEmail"
                name="guestEmail"
                type="email"
                value={form.guestEmail}
                onChange={handleChange}
                readOnly={isProfileContactFieldLocked('guestEmail', user)}
                className={isProfileContactFieldLocked('guestEmail', user) ? 'visit-us-input-profile' : undefined}
              />
            </div>
            <div className="visit-us-field">
              <label htmlFor="guestPhone">Phone *</label>
              <input
                id="guestPhone"
                name="guestPhone"
                type="tel"
                value={form.guestPhone}
                onChange={handleChange}
                readOnly={isProfileContactFieldLocked('guestPhone', user)}
                className={isProfileContactFieldLocked('guestPhone', user) ? 'visit-us-input-profile' : undefined}
              />
            </div>
            <div className="visit-us-field">
              <label htmlFor="preferredContact">Preferred contact</label>
              <select id="preferredContact" name="preferredContact" value={form.preferredContact} onChange={handleChange}>
                {PREFERRED_CONTACT_OPTIONS.map((opt) => (
                  <option key={opt.value || 'none'} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="visit-us-grid visit-us-grid-3 visit-us-address-grid">
            <div className="visit-us-field visit-us-span-2">
              <label htmlFor="guestAddress">Street address</label>
              <input
                id="guestAddress"
                name="guestAddress"
                value={form.guestAddress}
                onChange={handleChange}
                readOnly={isProfileContactFieldLocked('guestAddress', user)}
                className={isProfileContactFieldLocked('guestAddress', user) ? 'visit-us-input-profile' : undefined}
              />
            </div>
            <div className="visit-us-field">
              <label htmlFor="guestAddress2">Apt / suite</label>
              <input
                id="guestAddress2"
                name="guestAddress2"
                value={form.guestAddress2}
                onChange={handleChange}
                readOnly={isProfileContactFieldLocked('guestAddress2', user)}
                className={isProfileContactFieldLocked('guestAddress2', user) ? 'visit-us-input-profile' : undefined}
              />
            </div>
            <div className="visit-us-field">
              <label htmlFor="guestCity">City</label>
              <input
                id="guestCity"
                name="guestCity"
                value={form.guestCity}
                onChange={handleChange}
                readOnly={isProfileContactFieldLocked('guestCity', user)}
                className={isProfileContactFieldLocked('guestCity', user) ? 'visit-us-input-profile' : undefined}
              />
            </div>
            <div className="visit-us-field">
              <label htmlFor="guestState">State</label>
              <select
                id="guestState"
                name="guestState"
                value={form.guestState}
                onChange={handleChange}
                disabled={isProfileContactFieldLocked('guestState', user)}
                className={isProfileContactFieldLocked('guestState', user) ? 'visit-us-input-profile' : undefined}
              >
                <option value="">State</option>
                {US_STATES.map(([code, name]) => (
                  <option key={code} value={code}>
                    {name}
                  </option>
                ))}
              </select>
            </div>
            <div className="visit-us-field">
              <label htmlFor="guestZipCode">ZIP</label>
              <input
                id="guestZipCode"
                name="guestZipCode"
                value={form.guestZipCode}
                onChange={handleChange}
                maxLength={10}
                readOnly={isProfileContactFieldLocked('guestZipCode', user)}
                className={isProfileContactFieldLocked('guestZipCode', user) ? 'visit-us-input-profile' : undefined}
              />
            </div>
          </div>
        </section>

        {form.type === 'purchase' && (
          <section className="visit-us-section">
            <h3>What you&apos;re looking for</h3>
            <div className="visit-us-grid visit-us-grid-4">
              <div className="visit-us-field">
                <label htmlFor="interestMake">Make</label>
                <select id="interestMake" name="interestMake" value={form.interestMake} onChange={handleChange}>
                  <option value="">Any</option>
                  {CAR_MAKES.map((make) => (
                    <option key={make} value={make}>
                      {make}
                    </option>
                  ))}
                </select>
              </div>
              <div className="visit-us-field">
                <label htmlFor="interestModel">Model</label>
                <select
                  id="interestModel"
                  name="interestModel"
                  value={form.interestModel}
                  onChange={handleChange}
                  disabled={!form.interestMake}
                >
                  <option value="">{form.interestMake ? 'Any' : 'Select make first'}</option>
                  {interestModelOptions.map((model) => (
                    <option key={model} value={model}>
                      {model}
                    </option>
                  ))}
                </select>
              </div>
              <div className="visit-us-field">
                <label htmlFor="interestTrim">Trim (optional)</label>
                <select
                  id="interestTrim"
                  name="interestTrim"
                  value={form.interestTrim}
                  onChange={handleChange}
                  disabled={!form.interestModel}
                >
                  <option value="">{form.interestModel ? 'Any' : 'Select model first'}</option>
                  {interestTrimOptions.map((trim) => (
                    <option key={trim} value={trim}>
                      {trim}
                    </option>
                  ))}
                </select>
              </div>
              <div className="visit-us-field">
                <label htmlFor="interestYearMin">Year from</label>
                <select id="interestYearMin" name="interestYearMin" value={form.interestYearMin} onChange={handleChange}>
                  <option value="">Any</option>
                  {YEAR_OPTIONS.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
              <div className="visit-us-field">
                <label htmlFor="interestYearMax">Year to</label>
                <select id="interestYearMax" name="interestYearMax" value={form.interestYearMax} onChange={handleChange}>
                  <option value="">Any</option>
                  {YEAR_OPTIONS.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
              <div className="visit-us-field">
                <label htmlFor="interestMileageMax">Max mileage</label>
                <input
                  id="interestMileageMax"
                  name="interestMileageMax"
                  type="number"
                  min="0"
                  value={form.interestMileageMax}
                  onChange={handleChange}
                  placeholder="80000"
                />
              </div>
              <div className="visit-us-field">
                <label htmlFor="budgetMax">Max budget</label>
                <div className="visit-us-currency">
                  <span>$</span>
                  <input
                    id="budgetMax"
                    name="budgetMax"
                    type="text"
                    inputMode="numeric"
                    value={formatDollarWholeDisplay(form.budgetMax)}
                    onChange={handleChange}
                    placeholder="25,000"
                  />
                </div>
              </div>
              <div className="visit-us-field visit-us-span-2">
                <label htmlFor="carId">Specific lot vehicle (optional)</label>
                <select id="carId" name="carId" value={form.carId} onChange={handleChange}>
                  <option value="">No specific vehicle</option>
                  {availableCars.map((car) => (
                    <option key={car.id} value={car.id}>
                      {car.year} {car.make} {car.model} #{car.stockNumber}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </section>
        )}

        {form.type === 'sell' && (
          <section className="visit-us-section">
            <h3>Your vehicle</h3>
            <div className="visit-us-grid visit-us-grid-3">
              <div className="visit-us-field">
                <label htmlFor="sellYear">Year *</label>
                <select id="sellYear" name="sellYear" value={form.sellYear} onChange={handleChange} required>
                  <option value="">Select year</option>
                  {YEAR_OPTIONS.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
              <div className="visit-us-field">
                <label htmlFor="sellMake">Make *</label>
                <select id="sellMake" name="sellMake" value={form.sellMake} onChange={handleChange} required>
                  <option value="">Select make</option>
                  {CAR_MAKES.map((make) => (
                    <option key={make} value={make}>
                      {make}
                    </option>
                  ))}
                </select>
              </div>
              <div className="visit-us-field">
                <label htmlFor="sellModel">Model *</label>
                <select
                  id="sellModel"
                  name="sellModel"
                  value={form.sellModel}
                  onChange={handleChange}
                  required
                  disabled={!form.sellMake}
                >
                  <option value="">{form.sellMake ? 'Select model' : 'Select make first'}</option>
                  {sellModelOptions.map((model) => (
                    <option key={model} value={model}>
                      {model}
                    </option>
                  ))}
                </select>
              </div>
              <div className="visit-us-field">
                <label htmlFor="sellTrim">Trim</label>
                <select id="sellTrim" name="sellTrim" value={form.sellTrim} onChange={handleChange}>
                  <option value="">Optional</option>
                  {TRIM_LEVELS.map((trim) => (
                    <option key={trim} value={trim}>
                      {trim}
                    </option>
                  ))}
                </select>
              </div>
              <div className="visit-us-field">
                <label htmlFor="sellMileage">Mileage *</label>
                <input
                  id="sellMileage"
                  name="sellMileage"
                  type="number"
                  min="0"
                  value={form.sellMileage}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="visit-us-field">
                <label htmlFor="sellCondition">Condition *</label>
                <select id="sellCondition" name="sellCondition" value={form.sellCondition} onChange={handleChange}>
                  {CONDITION_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="visit-us-field">
                <label htmlFor="sellVin">VIN</label>
                <input id="sellVin" name="sellVin" value={form.sellVin} onChange={handleChange} maxLength={17} />
              </div>
              <div className="visit-us-field">
                <label htmlFor="sellExpectedPrice">Asking price</label>
                <div className="visit-us-currency">
                  <span>$</span>
                  <input
                    id="sellExpectedPrice"
                    name="sellExpectedPrice"
                    type="text"
                    inputMode="numeric"
                    value={formatDollarWholeDisplay(form.sellExpectedPrice)}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div className="visit-us-field">
                <label htmlFor="sellTitleStatus">Title status</label>
                <select id="sellTitleStatus" name="sellTitleStatus" value={form.sellTitleStatus} onChange={handleChange}>
                  {TITLE_STATUS_OPTIONS.map((opt) => (
                    <option key={opt.value || 'none'} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </section>
        )}

        <section className="visit-us-section">
          <h3>ID photos (optional)</h3>
          <p className="visit-us-hint">Optional — upload now or bring your ID when you visit.</p>
          <div className="visit-us-id-row">
            <div className="visit-us-id-card">
              <label>Front of ID</label>
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                id="visit-id-front"
                className="visit-us-file-input"
                onChange={(e) => handleIdFile('front', e)}
              />
              <label htmlFor="visit-id-front" className="visit-us-upload-btn">
                <Camera size={16} /> Choose photo
              </label>
              {idFrontPreview && <img src={idFrontPreview} alt="ID front" className="visit-us-id-preview" />}
            </div>
            <div className="visit-us-id-card">
              <label>Back of ID</label>
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                id="visit-id-back"
                className="visit-us-file-input"
                onChange={(e) => handleIdFile('back', e)}
              />
              <label htmlFor="visit-id-back" className="visit-us-upload-btn">
                <Camera size={16} /> Choose photo
              </label>
              {idBackPreview && <img src={idBackPreview} alt="ID back" className="visit-us-id-preview" />}
            </div>
          </div>
        </section>

        <section className="visit-us-section">
          <h3>Notes</h3>
          <div className="visit-us-field">
            <textarea
              id="notes"
              name="notes"
              value={form.notes}
              onChange={handleChange}
              rows={3}
              placeholder="Anything else you'd like us to know…"
            />
          </div>
        </section>

        <div className="visit-us-actions">
          <button type="submit" className="visit-us-submit" disabled={submitting}>
            {submitting ? (
              'Saving…'
            ) : (
              <>
                <Save size={18} />
                Continue to secure payment
              </>
            )}
          </button>
        </div>
      </form>
      )}
    </div>
  );
}

export default VisitUsTab;
